import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  NotificationsActive as AppIcon,
  Http as WebhookIcon,
} from '@mui/icons-material';
import { Contact } from '../../types/contact';

interface ContactListProps {
  contacts: Contact[];
  onView: (contact: Contact) => void;
  onEdit: (contact: Contact) => void;
  onDelete: (id: number) => void;
}

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  onView,
  onEdit,
  onDelete,
}) => {
  // 연락 방법에 따른 아이콘 반환
  const getContactTypeIcon = (type: Contact['type']) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'sms':
        return <PhoneIcon fontSize="small" />;
      case 'app':
        return <AppIcon fontSize="small" />;
      case 'webhook':
        return <WebhookIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // 연락 방법 표시 텍스트
  const getContactTypeLabel = (type: Contact['type']) => {
    switch (type) {
      case 'email':
        return '이메일';
      case 'sms':
        return 'SMS';
      case 'app':
        return '앱 알림';
      case 'webhook':
        return '웹훅';
      default:
        return type;
    }
  };

  // 활성화 상태에 따른 색상
  const getStatusColor = (isActive: boolean): 'success' | 'error' => {
    return isActive ? 'success' : 'error';
  };

  if (contacts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          등록된 담당자 없음
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>이름</TableCell>
            <TableCell>연락 방법</TableCell>
            <TableCell>연락처/주소</TableCell>
            <TableCell>역할</TableCell>
            <TableCell>상태</TableCell>
            <TableCell align="right">작업</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contacts.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell>{contact.name}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getContactTypeIcon(contact.type)}
                  {getContactTypeLabel(contact.type)}
                </Box>
              </TableCell>
              <TableCell>{contact.target}</TableCell>
              <TableCell>{contact.role}</TableCell>
              <TableCell>
                <Chip
                  label={contact.is_active ? '활성' : '비활성'}
                  color={getStatusColor(contact.is_active)}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="상세 보기">
                  <IconButton
                    size="small"
                    onClick={() => onView(contact)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="수정">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(contact)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="삭제">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(contact.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ContactList; 